/**
 * Configuration API Service
 *
 * Handles all API calls for per-session (project-scoped) configuration
 * management. The tenant-wide variants were retired by FU-002A.
 */

import axios from 'axios';
import { config } from '../config/env';

const API_BASE_URL = config.BACKEND_URL;

/**
 * Validate a configuration before saving. Shared endpoint — applies to
 * both tenant-template (legacy) and session-scoped configurations.
 */
export const validateConfiguration = async (configuration) => {
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/admin/configuration/validate`,
    { configuration },
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Get default ISO 21434 configuration template. Used to seed a fresh
 * session-scoped configuration with the standard defaults.
 */
export const getDefaultConfiguration = async () => {
  const response = await axios.get(`${API_BASE_URL}/api/v1/admin/configuration/defaults`, {
    withCredentials: true
  });
  return response.data;
};

// =====================================================================
// PER-SESSION (PROJECT-SCOPED) CONFIGURATION ENDPOINTS
//
// Operate on a specific TARA analysis (project workspace
// configuration tab) via the canonical /api/v1/sessions/{sessionId}/* routes.
// =====================================================================

/**
 * Get the active configuration snapshot for a specific TARA session (project).
 */
export const getSessionActiveConfiguration = async (sessionId) => {
  if (!sessionId) {
    throw new Error('getSessionActiveConfiguration requires a sessionId.');
  }
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/active`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Get version history for a session's configuration.
 */
export const getSessionConfigurationHistory = async (sessionId) => {
  if (!sessionId) {
    throw new Error('getSessionConfigurationHistory requires a sessionId.');
  }
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/history`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Get a specific configuration version snapshot for a session.
 */
export const getSessionConfigurationByVersion = async (sessionId, version) => {
  if (!sessionId) {
    throw new Error('getSessionConfigurationByVersion requires a sessionId.');
  }
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/version/${version}`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Update a session's configuration (creates and activates a new version
 * within the session scope).
 */
export const updateSessionConfiguration = async (sessionId, configuration, metadata) => {
  if (!sessionId) {
    throw new Error('updateSessionConfiguration requires a sessionId.');
  }
  const response = await axios.put(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/update`,
    { configuration, metadata },
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Activate a specific configuration version within a session scope.
 */
export const activateSessionConfiguration = async (sessionId, version) => {
  if (!sessionId) {
    throw new Error('activateSessionConfiguration requires a sessionId.');
  }
  const response = await axios.post(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/activate/${version}`,
    {},
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Delete an inactive configuration version from a session.
 */
export const deleteSessionConfigurationVersion = async (sessionId, version) => {
  if (!sessionId) {
    throw new Error('deleteSessionConfigurationVersion requires a sessionId.');
  }
  const response = await axios.delete(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/version/${version}`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Get configuration audit log entries for a session.
 */
export const getSessionConfigurationAuditLog = async (sessionId, limit = 50) => {
  if (!sessionId) {
    throw new Error('getSessionConfigurationAuditLog requires a sessionId.');
  }
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/sessions/${encodeURIComponent(sessionId)}/configuration/audit-log?limit=${limit}`,
    { withCredentials: true }
  );
  return response.data;
};

/**
 * Impact-rating criteria for the tenant.
 *
 * Every (category × level) pair — Safety/Severe, Financial/Major, … — carries
 * the configured `criteria` text, `examples`, and (for Financial) a
 * `monetary_range`. Used to explain WHY a rating means what it does, straight
 * from the tenant's own configured methodology rather than model prose.
 */
export const getImpactRatingConfiguration = async () => {
  const response = await axios.get(
    `${API_BASE_URL}/api/v1/configuration/impact-rating`,
    { withCredentials: true },
  );
  return response.data;
};
